import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { api } from '@/lib/api';

interface CohortDataPoint {
  cohortId: string; // e.g., "Dec 04", "Nov 27"
  cohortDate: string; // YYYYMMDD format
  week: number; // 0, 1, 2, 3...
  activeUsers: number;
  totalUsers: number;
  retentionRate: number; // percentage 0-100
}

interface CohortSummary {
  cohortId: string;
  cohortDate: string;
  totalUsers: number;
  weeklyData: Array<{
    week: number;
    activeUsers: number;
    retentionRate: number;
  }>;
}

/**
 * Fetch GA4 cohort retention data
 * Shows user retention by weekly cohorts over 8 weeks
 */
export function useGA4CohortRetention(numCohorts = 8, numWeeks = 8) {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;

  return useQuery({
    queryKey: ['ga4-cohort-retention', ga4PropertyId, numCohorts, numWeeks],
    queryFn: async (): Promise<{ cohorts: CohortSummary[]; raw: CohortDataPoint[] } | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      try {
        // Build cohort definitions for the last N weeks
        const cohorts = [];
        const today = new Date();

        for (let i = 0; i < numCohorts; i++) {
          const startDate = new Date(today);
          startDate.setDate(today.getDate() - (i + 1) * 7);
          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);

          const formatDate = (d: Date) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          cohorts.push({
            name: `week${i}`,
            dimension: 'firstSessionDate',
            dateRange: {
              startDate: formatDate(startDate),
              endDate: formatDate(endDate),
            },
          });
        }

        // Note: For cohort requests, dateRanges must NOT be included
        // The date ranges are specified in the cohortSpec.cohorts instead
        const request = {
          propertyId: ga4PropertyId,
          cohortSpec: {
            cohorts,
            cohortsRange: {
              granularity: 'WEEKLY',
              startOffset: 0,
              endOffset: numWeeks - 1,
            },
          },
          dimensions: ['cohort', 'cohortNthWeek'],
          metrics: ['cohortActiveUsers', 'cohortTotalUsers'],
        };

        console.log('Cohort request:', JSON.stringify(request, null, 2));

        const response = await api.google.runGAReport(request);
        console.log('Cohort response:', JSON.stringify(response, null, 2));

        if (!response?.rows || response.rows.length === 0) {
          console.log('No cohort rows returned');
          return { cohorts: [], raw: [] };
        }

        // Parse raw data
        const raw: CohortDataPoint[] = response.rows.map((row: any) => {
          const cohortName = row.dimensionValues?.[0]?.value || '';
          const week = parseInt(row.dimensionValues?.[1]?.value || '0', 10);
          const activeUsers = parseInt(row.metricValues?.[0]?.value || '0', 10);
          const totalUsers = parseInt(row.metricValues?.[1]?.value || '0', 10);

          // Get cohort index from name (e.g., "week0" -> 0)
          const cohortIndex = parseInt(cohortName.replace('week', ''), 10);

          // Calculate the start date for this cohort
          const cohortStartDate = new Date(today);
          cohortStartDate.setDate(today.getDate() - (cohortIndex + 1) * 7);

          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const cohortId = `${monthNames[cohortStartDate.getMonth()]} ${String(cohortStartDate.getDate()).padStart(2, '0')}`;

          const formatDateYYYYMMDD = (d: Date) => {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
          };

          return {
            cohortId,
            cohortDate: formatDateYYYYMMDD(cohortStartDate),
            week,
            activeUsers,
            totalUsers,
            retentionRate: totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0,
          };
        });

        // Group by cohort
        const cohortMap = new Map<string, CohortSummary>();

        for (const point of raw) {
          if (!cohortMap.has(point.cohortId)) {
            // Find total users from week 0
            const week0 = raw.find(r => r.cohortId === point.cohortId && r.week === 0);
            cohortMap.set(point.cohortId, {
              cohortId: point.cohortId,
              cohortDate: point.cohortDate,
              totalUsers: week0?.totalUsers || point.totalUsers,
              weeklyData: [],
            });
          }

          const cohort = cohortMap.get(point.cohortId)!;
          cohort.weeklyData.push({
            week: point.week,
            activeUsers: point.activeUsers,
            retentionRate: point.retentionRate,
          });
        }

        // Sort weekly data and cohorts
        const cohortSummaries = Array.from(cohortMap.values()).map(c => ({
          ...c,
          weeklyData: c.weeklyData.sort((a, b) => a.week - b.week),
        })).sort((a, b) => b.cohortDate.localeCompare(a.cohortDate)); // Most recent first

        return { cohorts: cohortSummaries, raw };
      } catch (error: any) {
        console.error('Failed to fetch GA4 cohort data:', error);
        console.error('Error details:', error?.message || error);
        return { cohorts: [], raw: [] };
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 15, // 15 minutes - cohort data doesn't change often
  });
}

/**
 * Get average retention by week across all cohorts
 */
export function useGA4AverageRetention() {
  const { data: cohortData } = useGA4CohortRetention();

  if (!cohortData?.cohorts) return null;

  const weeklyAverages: Array<{ week: number; avgRetention: number }> = [];

  // Find max weeks
  const maxWeeks = Math.max(...cohortData.cohorts.flatMap(c => c.weeklyData.map(w => w.week)));

  for (let week = 0; week <= maxWeeks; week++) {
    const retentions = cohortData.cohorts
      .map(c => c.weeklyData.find(w => w.week === week)?.retentionRate)
      .filter((r): r is number => r !== undefined);

    if (retentions.length > 0) {
      weeklyAverages.push({
        week,
        avgRetention: retentions.reduce((a, b) => a + b, 0) / retentions.length,
      });
    }
  }

  return weeklyAverages;
}
